// Model configuration types

import { ReferenceMode } from 'containers/SubSidebar/AIVideoContent/consts'
import { TMediaItemType } from './media'

export interface IAIVideoModelOption {
  duration: number
  qualities: [
    {
      quality: number
      credit: number
      generationAvgDurationSeconds: number
    }
  ]
}

export interface IAIVideoModelDefaultValues {
  quality: number
  duration: number
}

export interface IFirstAndLastFrameRuleLeaf {
  field: 'videoDurationSeconds' | 'quality' | 'aspectRatio'
  value: string | number
}

export type TFirstAndLastFrameRule =
  | IFirstAndLastFrameRuleLeaf
  | { match: 'all' | 'any'; rules: TFirstAndLastFrameRule[] }

export type TFirstAndLastFrameSupport =
  | { isSupported: false }
  | { isSupported: true; conditions?: TFirstAndLastFrameRule }

export interface IMultipleReferenceMediasCapability {
  isSupported: boolean
  maxTotalCount?: number
  images?: { maxCount: number }
  videos?: { maxCount: number; maxLengthSeconds: number }
  audios?: { maxCount: number }
  limits?: { totalWhenMixed: number }
}

export interface ISupportedMediaMimeTypes {
  images?: string[]
  videos?: string[]
  audios?: string[]
}

export interface IAIVideoModelCapabilities {
  aspectRatios: string[] | null
  qualities: number[] | null
  isMediaResizingSupported: boolean
  isAudioGenerationSupported: boolean
  firstAndLastFrameSupport: TFirstAndLastFrameSupport
  multipleReferenceMedias?: IMultipleReferenceMediasCapability
  supportedMediaMimeTypes?: ISupportedMediaMimeTypes
}

export interface IAIVideoModel {
  id: number
  name: string
  code: string
  model: string
  capabilities: IAIVideoModelCapabilities
  options: IAIVideoModelOption[]
  order: number
  isDefaultFor: string[]
  defaultValues: IAIVideoModelDefaultValues
  tariffPlans: number[]
  description: string
}

export enum ReferenceMediaImageType {
  FIRST_FRAME = 'firstFrame',
  LAST_FRAME = 'lastFrame',
  REFERENCE_IMAGE = 'referenceImage'
}

export interface IReferenceMediaDimensions {
  width: number
  height: number
}

export type TReferenceMode = ReferenceMode

export interface IReferenceMediaImage {
  type: ReferenceMediaImageType
  url: string
  dimensions?: IReferenceMediaDimensions
  nativeRatio?: string
}

export interface IReferenceMediaItem {
  id: string
  type: ReferenceMediaImageType
  url: string
  isLoading: boolean
  dimensions?: IReferenceMediaDimensions
  nativeRatio?: string
}

export interface IReferenceMedias {
  frames?: Array<IReferenceMediaItem>
  images?: Array<IReferenceMediaItem>
}

export interface IApiReferenceMedias {
  images?: Array<IReferenceMediaImage>
}

export interface IAIVideoGenerationParams {
  referenceMedias?: IReferenceMedias
  selectedModel: string | null
  duration: number
  aspectRatio: string | null
  quality: number
  prompt: string
  targetMediaItemId: string | null
  aiImageId: number | null
  isConsistencyEnabled: boolean
  smartPrompt: string
  refImageSelectedStyleId?: number
  sourceProjectId?: number
  parentProjectId?: number
  uploadedImagePath?: string
}

// API types
export interface IAIVideoGenerationRequestBody {
  id?: number // Id of Ai gen project, for enabled consistent generation
  referenceMedias?: IApiReferenceMedias
  prompt: string
  videoDurationSeconds: number
  videoModel: string
  aspectRatio?: string
  quality: number
  meta?: IAIVideoProjectMeta
}

export interface IAIVideoProjectMeta {
  reference?: IAIVideoProjectReference
  sourceProjectId?: number
  imageStyleId?: number
  parentProjectId?: number
}

export interface IAIVideoProjectReference {
  id: number
  type: TMediaItemType
}

export enum AIVideoSocketMessageStatus {
  COMPLETED = 'completed',
  FAILED = 'failed',
  PROCESSING = 'processing'
}

export enum AIVideoSocketMessageState {
  ERROR = 'error',
  FINISHED = 'finished'
}

export interface IAIVideoSocketMessage {
  id: number
  referenceMedias: IApiReferenceMedias
  userId: number
  prompt: string
  status: AIVideoSocketMessageStatus
  state: AIVideoSocketMessageState
  aspectRatio: string
  quality: number
  createdAt: string
  externalVideoId: string
  type?: 'ai-video'
  updatedAt: string
  videoDurationSeconds: number
  videoSource: string
  videoModel: string
  videoUrl: string
  width: number | null
  height: number | null
  failureReason: string
  meta?: IAIVideoProjectMeta
  gen_ai_video_hash: string
  generatedBy: string
}

export interface IAIVideoHistoryState {
  videos: IAIVideoProject[]
  isLoading: boolean
  isDataInitiallyLoaded: boolean
  totalCount: number
  rawOffset: number
  isScopedToCurrentProject: boolean
}

export interface IAIVideoListParams {
  limit?: number
  offset?: number
  projectIds?: number[]
  videoProjectId?: number
}

export interface IAIVideoProject {
  id: number | string
  referenceMedias: IApiReferenceMedias
  userId: number
  prompt: string
  status: AIVideoSocketMessageStatus | null
  aspectRatio: string | null
  quality: number
  createdAt?: string
  externalVideoId?: string | null
  type?: string
  updatedAt?: string
  videoDurationSeconds: number
  videoSource: string
  videoModel: string
  videoUrl: string
  generatedBy: string
  width: number | null
  height: number | null
  meta?: IAIVideoProjectMeta
  imageUrl?: string
  thumbnail?: string | null
}

export interface IAIVideoGenerationResponse {
  genAiVideoHash: string
}
